import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import api from '../../utils/Api';

import {
    FaSun,
    FaMoon,
    FaBriefcase,
    FaClipboardList,
    FaHome,
    FaBars,
    FaTimes,
    FaSignOutAlt,
    FaFile,
    FaUser,
    FaCog,
    FaChevronDown,
    FaDashcube,
    FaSpinner,
    FaChevronLeft,
    FaChevronRight,
    FaCalendar
} from 'react-icons/fa';


const DashboardLayout = ({ children, onLogout, user: propUser, isAuthenticated: propIsAuthenticated }) => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const [theme, setTheme] = useState(
        localStorage.getItem('theme') ||
        (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
    );

    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();

    // Get authentication state from Redux store but prioritize props
    const { user: reduxUser, token, loading, error } = useSelector((state) => state.auth);

    // Use props if provided, otherwise fallback to Redux
    const user = propUser || reduxUser;
    const isAuthenticated = propIsAuthenticated !== undefined ? propIsAuthenticated : (!!token && !!user);

    // Navigation items for the sidebar
    const sidebarItems = [
        {
            title: 'Accueil',
            path: '/',
            icon: <FaHome className="text-lg" />,
            description: 'Page principale',
        },
        {
            title: 'Tableau de bord',
            path: '/dashboard',
            icon: <FaDashcube className="text-lg" />,
            description: 'Vue d\'ensemble',
        },
        {
            title: 'Mes Offres',
            path: '/dashboard/job-offers',
            icon: <FaBriefcase className="text-lg" />,
            description: 'Offres d\'emploi',
        },
        {
            title: 'Freelance',
            path: '/dashboard/missions',
            icon: <FaBriefcase className="text-lg" />,
            description: 'Missions freelance',
            feature: 'missions'
        },
        {
            title: 'Candidatures',
            path: '/dashboard/applications',
            icon: <FaClipboardList className="text-lg" />,
            description: 'Gestion des candidatures',
        },
        {
            title: 'Calendrier',
            path: '/dashboard/calendar',
            icon: <FaCalendar className="text-lg" />,
            description: 'Gestion des rendez-vous',
        },
        {
            title: 'CVthèque',
            path: '/dashboard/cvtheque',
            icon: <FaFile className="text-lg" />,
            description: 'Sélection des candidats',
            feature: 'cvtheque'
        },
        {
            title: 'Mon Profil',
            path: '/dashboard/profile',
            icon: <FaUser className="text-lg" />,
            description: 'Gérer mon profil',
        },
        {
            title: 'Paramètres',
            path: '/dashboard/settings',
            icon: <FaCog className="text-lg" />,
            description: 'Configuration',
        },
    ].filter(item => {
        if (item.feature) {
            return user?.features?.[item.feature];
        }
        return true;
    });

    useEffect(() => {
        // Theme management
        const root = window.document.documentElement;
        root.classList.remove('light', 'dark');
        root.classList.add(theme);
        localStorage.setItem('theme', theme);

        // Close user menu when clicking outside
        const handleClickOutside = (event) => {
            if (isUserMenuOpen && !event.target.closest('.user-menu-container')) {
                setIsUserMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [theme, isUserMenuOpen]);

    const toggleTheme = () => {
        setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
    };

    const handleLogout = async () => {
        try {
            setIsLoggingOut(true);
            setIsUserMenuOpen(false);
            setIsMobileSidebarOpen(false);

            await new Promise(resolve => setTimeout(resolve, 1500));

            if (onLogout) {
                await onLogout();
            } else {
                dispatch({ type: 'auth/logout' });
            }

            navigate('/', { replace: true });

        } catch (error) {
            console.error('Logout error:', error);
            window.location.href = '/';
        } finally {
            setIsLoggingOut(false);
        }
    };

    const [profileImageUrl, setProfileImageUrl] = useState('/pictures/avatar.avif');

    useEffect(() => {
        const fetchProfileImage = async () => {
            if (user?.profile_picture) {
                if (user.profile_picture.startsWith('http')) {
                    setProfileImageUrl(user.profile_picture);
                } else {
                    try {
                        const pathParts = user.profile_picture.split('/');
                        const filename = pathParts.pop();
                        const folder = pathParts.length > 0 ? pathParts.join('/') : 'profile_pictures';

                        const response = await api.get(`/files/${folder}/${filename}`, {
                            responseType: 'blob',
                        });

                        const blob = new Blob([response.data], { type: response.headers['content-type'] });
                        const objectUrl = window.URL.createObjectURL(blob);
                        setProfileImageUrl(objectUrl);

                        // Cleanup function to revoke the object URL when component unmounts or image changes
                        return () => window.URL.revokeObjectURL(objectUrl);
                    } catch (err) {
                        console.error("Error fetching image blob", err);
                        setProfileImageUrl('/pictures/avatar.avif');
                    }
                }
            } else {
                setProfileImageUrl('/pictures/avatar.avif');
            }
        };

        fetchProfileImage();
    }, [user?.profile_picture]);

    const getUserDisplayName = () => {
        if (!user) return '';
        return user.company_name || user.name || user.email?.split('@')[0] || 'Utilisateur';
    };

    const isActivePath = (path) => {
        return location.pathname === path;
    };

    return (
        <div className="bg-gray-50 dark:bg-gray-900">
            {/* Top Navigation Bar - Dashboard specific */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 dark:bg-gray-900/90 backdrop-blur-md shadow-sm border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between h-16 px-4">
                    {/* Left side - Mobile menu button and logo */}
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={() => setIsMobileSidebarOpen(true)}
                            className="md:hidden text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors duration-300"
                            aria-label="Ouvrir le menu"
                        >
                            <FaBars className="h-6 w-6" />
                        </button>

                        {/* Desktop sidebar toggle */}
                        <button
                            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                            className="hidden md:block text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors duration-300"
                            aria-label="Basculer la sidebar"
                        >
                            {isSidebarOpen ? <FaChevronLeft className="h-5 w-5" /> : <FaChevronRight className="h-5 w-5" />}
                        </button>

                        <Link
                            to="/"
                            className="text-xl font-bold tracking-tight text-gray-800 dark:text-gray-200 hover:text-gray-900 dark:hover:text-gray-100 transition-colors duration-300"
                        >
                            <img
                                src="/logo.png"
                                alt="Logo"
                                className="h-10 w-auto"
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'block';
                                }}
                            />
                            <span className="hidden">Tekhdem</span>
                        </Link>
                    </div>

                    {/* Right side - Theme toggle and user menu */}
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={toggleTheme}
                            className="p-2 rounded-full text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-300"
                            aria-label={`Basculer vers le mode ${theme === 'dark' ? 'clair' : 'sombre'}`}
                        >
                            {theme === 'dark' ? <FaSun className="text-yellow-400" /> : <FaMoon className="text-gray-800" />}
                        </button>

                        {isAuthenticated && user ? (
                            <div className="relative user-menu-container">
                                <button
                                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                                    className="flex items-center space-x-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 p-2 rounded-lg transition-colors duration-300"
                                    aria-expanded={isUserMenuOpen}
                                    aria-haspopup="true"
                                    disabled={isLoggingOut}
                                >
                                    <img
                                        src={profileImageUrl}
                                        alt={`Avatar de ${getUserDisplayName()}`}
                                        className="h-8 w-8 rounded-full border-2 border-gray-300 dark:border-gray-600 object-cover"
                                        onError={(e) => {
                                            e.target.src = "/pictures/avatar.avif";
                                        }}
                                    />
                                    <span className="text-sm font-medium hidden sm:block">
                                        {getUserDisplayName()}
                                    </span>
                                    <FaChevronDown
                                        className={`text-xs transition-transform duration-200 ${isUserMenuOpen ? 'rotate-180' : ''
                                            }`}
                                    />
                                </button>

                                {/* User Dropdown Menu */}
                                {isUserMenuOpen && (
                                    <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50">
                                        <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                                            <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                                {getUserDisplayName()}
                                            </p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                                {user.email}
                                            </p>
                                        </div>

                                        <Link
                                            to="/dashboard/profile"
                                            onClick={() => setIsUserMenuOpen(false)}
                                            className="flex items-center w-full px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
                                        >
                                            <FaUser className="mr-3 text-sm" />
                                            Mon Profil
                                        </Link>

                                        <Link
                                            to="/dashboard/settings"
                                            onClick={() => setIsUserMenuOpen(false)}
                                            className="flex items-center w-full px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
                                        >
                                            <FaCog className="mr-3 text-sm" />
                                            Paramètres
                                        </Link>

                                        <hr className="my-1 border-gray-200 dark:border-gray-700" />

                                        <button
                                            onClick={handleLogout}
                                            disabled={isLoggingOut}
                                            className={`flex items-center w-full px-4 py-2 text-sm transition-colors duration-200 ${isLoggingOut
                                                ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
                                                : 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
                                                }`}
                                        >
                                            {isLoggingOut ? (
                                                <FaSpinner className="animate-spin text-sm mr-3" />
                                            ) : (
                                                <FaSignOutAlt className="mr-3 text-sm" />
                                            )}
                                            {isLoggingOut ? 'Déconnexion...' : 'Déconnexion'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="flex space-x-3">
                                <Link
                                    to="/login"
                                    className="px-4 py-2 text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg text-sm transition-colors duration-300"
                                >
                                    Connexion
                                </Link>
                                <Link
                                    to="/register"
                                    className="px-4 py-2 bg-primary-400 text-white hover:bg-primary-500 rounded-lg text-sm transition-colors duration-300 shadow-md hover:shadow-lg"
                                >
                                    Inscription
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            </nav>

            {/* Desktop Sidebar */}
            <div className={`fixed left-0 top-16 bottom-0 z-40 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transition-all duration-300 ${isSidebarOpen ? 'w-64' : 'w-16'
                } hidden md:block`}>
                <div className="flex flex-col h-full">
                    <nav className="flex-1 px-2 py-4 space-y-2">
                        {sidebarItems.map((item) => (
                            <Link
                                key={item.path}
                                to={item.path}
                                className={`flex items-center px-3 py-2 rounded-lg transition-colors duration-300 group ${isActivePath(item.path)
                                    ? 'bg-primary-100/20 dark:bg-primary-100/20 text-primary-700 dark:text-primary-300'
                                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                                    }`}
                            >
                                <div className="flex-shrink-0">
                                    {item.icon}
                                </div>
                                {isSidebarOpen && (
                                    <div className="ml-3 flex-1">
                                        <div className="text-sm font-medium">{item.title}</div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400">
                                            {item.description}
                                        </div>
                                    </div>
                                )}
                            </Link>
                        ))}
                    </nav>
                </div>
            </div>

            {/* Mobile Sidebar */}
            <Dialog
                open={isMobileSidebarOpen}
                onClose={() => setIsMobileSidebarOpen(false)}
                className="relative z-50 md:hidden"
            >
                <DialogBackdrop
                    transition
                    className="fixed inset-0 bg-black/40 transition-opacity duration-300 ease-in-out data-closed:opacity-0"
                />

                <div className="fixed inset-0 overflow-hidden">
                    <div className="absolute inset-0 overflow-hidden">
                        <div className="pointer-events-none fixed inset-y-0 left-0 flex max-w-full pr-10">
                            <DialogPanel
                                transition
                                className="pointer-events-auto relative w-screen max-w-sm transform transition duration-300 ease-in-out data-closed:-translate-x-full"
                            >
                                <div className="flex h-full flex-col bg-white dark:bg-gray-800 shadow-xl">
                                    <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                                        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Menu</h2>
                                        <button
                                            onClick={() => setIsMobileSidebarOpen(false)}
                                            className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
                                            aria-label="Fermer le menu"
                                        >
                                            <FaTimes className="h-6 w-6" />
                                        </button>
                                    </div>

                                    {/* User Info in Mobile Menu */}
                                    {isAuthenticated && user && (
                                        <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                                            <div className="flex items-center space-x-3">
                                                <img
                                                    src={profileImageUrl}
                                                    alt={`Avatar de ${getUserDisplayName()}`}
                                                    className="h-10 w-10 rounded-full border-2 border-gray-300 dark:border-gray-600 object-cover"
                                                    onError={(e) => {
                                                        e.target.src = "/pictures/avatar.avif";
                                                    }}
                                                />
                                                <div>
                                                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                                        {getUserDisplayName()}
                                                    </p>
                                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                                        {user.email}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <nav className="flex-1 px-4 py-4 space-y-2">
                                        {sidebarItems.map((item) => (
                                            <Link
                                                key={item.path}
                                                to={item.path}
                                                onClick={() => setIsMobileSidebarOpen(false)}
                                                className={`flex items-center px-3 py-3 rounded-lg transition-colors duration-300 ${isActivePath(item.path)
                                                    ? 'bg-primary-100/20 dark:bg-primary-100/20 text-primary-700 dark:text-primary-300'
                                                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                                                    }`}
                                            >
                                                <div className="flex-shrink-0 mr-3">
                                                    {item.icon}
                                                </div>
                                                <div>
                                                    <div className="text-sm font-medium">{item.title}</div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                                        {item.description}
                                                    </div>
                                                </div>
                                            </Link>
                                        ))}
                                    </nav>

                                    {/* Mobile logout button */}
                                    {isAuthenticated && (
                                        <div className="border-t border-gray-200 dark:border-gray-700 p-4">
                                            <button
                                                onClick={handleLogout}
                                                disabled={isLoggingOut}
                                                className={`w-full flex items-center justify-center px-4 py-3 rounded-lg transition-colors duration-300 ${isLoggingOut
                                                    ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
                                                    : 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
                                                    }`}
                                            >
                                                {isLoggingOut ? (
                                                    <FaSpinner className="animate-spin text-sm mr-3" />
                                                ) : (
                                                    <FaSignOutAlt className="mr-3 text-sm" />
                                                )}
                                                {isLoggingOut ? 'Déconnexion...' : 'Déconnexion'}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </DialogPanel>
                        </div>
                    </div>
                </div>
            </Dialog>

            {/* Main Content */}
            <div className={`transition-all duration-300 ${isSidebarOpen ? 'md:ml-64' : 'md:ml-16'
                } pt-16`}>
                <main className="">
                    {children}
                </main>
            </div>

            {/* Global Logout Loading Overlay */}
            {isLoggingOut && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 flex flex-col items-center space-y-4 shadow-xl">
                        <FaSpinner className="animate-spin text-2xl text-blue-500" />
                        <p className="text-gray-700 dark:text-gray-300 font-medium">
                            Déconnexion en cours...
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};


export default DashboardLayout;

